import { Component, input, OnInit, signal, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { WorkflowActionRulesComponent } from "./workflow.action.rules.component";

@TypeManagerDecorator("workflow-action-rules-step")
@Component({
    selector: "workflow-action-rules-step",
    template: `
        <div class="action-rules-step-container">
            <div class="step-header">
                <h3>Action Rules</h3>
                <p class="step-description">Configure rules for each action to control when they are executed.</p>
            </div>

            <div class="stages-list">
                @for (stage of workflowProcess().stages; track stage.workflowStageId || stage.id) {
                    @if (stage.actions && stage.actions.length > 0) {
                        <div class="stage-item">
                            <div class="stage-header" (click)="toggleStageExpansion(stage)">
                                <div class="stage-info">
                                    <span class="stage-name">{{ stage.stageName }}</span>
                                    <span class="actions-count">{{ stage.actions.length }} action(s)</span>
                                </div>
                                <div class="stage-actions">
                                    <span class="expand-icon" [class.expanded]="isStageExpanded(stage)">
                                        {{ isStageExpanded(stage) ? "▼" : "▶" }}
                                    </span>
                                </div>
                            </div>

                            @if (isStageExpanded(stage)) {
                                <div class="stage-content">
                                    <div class="actions-list">
                                        @for (action of stage.actions; track action.workflowActionId || action.id) {
                                            <div class="action-item">
                                                <div class="action-header" (click)="toggleActionExpansion(action)">
                                                    <div class="action-info">
                                                        <span class="action-name">{{
                                                            getActionDisplayName(action)
                                                        }}</span>
                                                        <span class="action-type">{{
                                                            getActionTypeDisplay(action)
                                                        }}</span>
                                                        <span class="stage-context">in {{ stage.stageName }}</span>
                                                        <span class="action-flags">
                                                            @if (action.defaultEnabled !== false) {
                                                                <span class="flag enabled">Enabled</span>
                                                            }
                                                        </span>
                                                    </div>
                                                    <div class="action-actions">
                                                        <span
                                                            class="expand-icon"
                                                            [class.expanded]="isActionExpanded(action)"
                                                        >
                                                            {{ isActionExpanded(action) ? "▼" : "▶" }}
                                                        </span>
                                                    </div>
                                                </div>

                                                @if (isActionExpanded(action)) {
                                                    <div class="action-content">
                                                        @if (
                                                            action.workflowActionId &&
                                                            workflowProcess().workflowProcessId
                                                        ) {
                                                            <workflow-action-rules
                                                                [selectedAction]="action"
                                                                [workflowProcessId]="
                                                                    workflowProcess().workflowProcessId
                                                                "
                                                            ></workflow-action-rules>
                                                        } @else {
                                                            <div class="rules-unavailable">
                                                                <p>
                                                                    Action rules will be available after saving the
                                                                    workflow process.
                                                                </p>
                                                            </div>
                                                        }
                                                    </div>
                                                }
                                            </div>
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                    }
                }
            </div>

            @if ((workflowProcess().stages || []).length === 0) {
                <div class="no-stages">
                    <p>No stages defined. Please add stages in the previous step.</p>
                </div>
            } @else if (!hasAnyActions()) {
                <div class="no-actions">
                    <p>No actions defined. Please add actions in the previous step.</p>
                </div>
            }
        </div>
    `,
    styles: `
        .action-rules-step-container {
            padding: 20px;
        }

        .step-header {
            margin-bottom: 24px;
        }

        .step-header h3 {
            margin: 0 0 8px 0;
            color: #333;
        }

        .step-description {
            margin: 0;
            color: #666;
            font-size: 14px;
        }

        .stages-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .stage-item {
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            overflow: hidden;
        }

        .stage-header {
            padding: 16px;
            background: #f8f9fa;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: background 0.2s;
        }

        .stage-header:hover {
            background: #e9ecef;
        }

        .stage-info {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .stage-name {
            font-weight: 500;
            color: #333;
        }

        .actions-count {
            font-size: 12px;
            color: #666;
            background: #e9ecef;
            padding: 2px 8px;
            border-radius: 12px;
        }

        .expand-icon {
            color: #666;
            font-size: 12px;
            transition: transform 0.2s;
        }

        .expand-icon.expanded {
            transform: rotate(90deg);
        }

        .stage-content {
            padding: 20px;
            border-top: 1px solid #e0e0e0;
        }

        .actions-list {
            display: flex;
            flex-direction: column;
            gap: 12px;
        }

        .action-item {
            background: #f8f9fa;
            border: 1px solid #e0e0e0;
            border-radius: 6px;
            overflow: hidden;
        }

        .action-header {
            padding: 12px 16px;
            background: white;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: background 0.2s;
        }

        .action-header:hover {
            background: #f8f9fa;
        }

        .action-info {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .action-name {
            font-weight: 500;
            color: #333;
        }

        .action-type {
            font-size: 11px;
            color: #666;
            background: #e9ecef;
            padding: 2px 6px;
            border-radius: 8px;
            text-transform: uppercase;
        }

        .stage-context {
            font-size: 12px;
            color: #666;
            font-style: italic;
        }

        .action-flags {
            display: flex;
            gap: 8px;
        }

        .flag {
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 500;
            text-transform: uppercase;
        }

        .flag.enabled {
            background: #d4edda;
            color: #155724;
        }

        .action-content {
            padding: 16px;
            border-top: 1px solid #e0e0e0;
        }

        .rules-unavailable {
            text-align: center;
            padding: 40px 20px;
            color: #666;
            background: #f8f9fa;
            border-radius: 6px;
        }

        .no-stages,
        .no-actions {
            text-align: center;
            padding: 40px 20px;
            color: #666;
        }
    `,
    standalone: true,
    imports: [CommonModule, WorkflowActionRulesComponent],
})
export class WorkflowActionRulesStepComponent implements OnInit {
    workflowProcess = input.required<WorkflowTypes.WorkflowProcess>();

    private expandedStages = signal<Set<number | string>>(new Set());
    private expandedActions = signal<Set<number | string>>(new Set());

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit() {}

    toggleStageExpansion(stage: WorkflowTypes.WorkflowStage) {
        const stageKey = stage.workflowStageId || stage.id!;
        const expanded = this.expandedStages();

        if (expanded.has(stageKey)) {
            expanded.delete(stageKey);
        } else {
            expanded.add(stageKey);
        }

        this.expandedStages.set(new Set(expanded));
    }

    isStageExpanded(stage: WorkflowTypes.WorkflowStage): boolean {
        const stageKey = stage.workflowStageId || stage.id!;
        return this.expandedStages().has(stageKey);
    }

    toggleActionExpansion(action: WorkflowTypes.WorkflowAction) {
        const actionKey = action.workflowActionId || action.id!;
        const expanded = this.expandedActions();

        if (expanded.has(actionKey)) {
            expanded.delete(actionKey);
        } else {
            expanded.add(actionKey);
        }

        this.expandedActions.set(new Set(expanded));
    }

    isActionExpanded(action: WorkflowTypes.WorkflowAction): boolean {
        const actionKey = action.workflowActionId || action.id!;
        return this.expandedActions().has(actionKey);
    }

    hasAnyActions(): boolean {
        return this.workflowProcess().stages?.some((stage) => stage.actions && stage.actions.length > 0) || false;
    }

    getActionDisplayName(action: WorkflowTypes.WorkflowAction): string {
        if (action.description && action.description.trim()) {
            return action.description;
        }

        if (action.actionCode) {
            return action.actionCode
                .replace(/_/g, " ")
                .toLowerCase()
                .replace(/\b\w/g, (l) => l.toUpperCase());
        }

        return `${action.actionType} Action`;
    }

    getActionTypeDisplay(action: WorkflowTypes.WorkflowAction): string {
        if (action.actionType === "SYSTEM_ACTION" && action.actionCode) {
            return action.actionCode
                .replace(/_/g, " ")
                .toLowerCase()
                .replace(/\b\w/g, (l) => l.toUpperCase());
        }
        return action.actionType;
    }
}
